<template>
  <button :disabled="isDisabled" :class="classes" @click="handleClick">{{ props.label }}</button>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    label: string;
    disabled?: boolean;
    slim?: boolean;
    version?: 'primary' | 'secondary' | 'highlight' | 'action' | 'grey' | 'white' | 'error' | 'primary--800';
  }>(),
  { label: 'button', disabled: false, slim: false }
);

const isDisabled = ref(props.disabled);

// Watch for changes in props.disabled and update isDisabled accordingly
watch(
  () => props.disabled,
  (newVal) => {
    isDisabled.value = newVal;
  }
);

const handleClick = () => {
  if (!props.disabled) {
    isDisabled.value = true;
    setTimeout(() => {
      isDisabled.value = props.disabled;
    }, 2000); // Disable for 2 seconds
  }
};

const classes = computed(() => ({
  button: true,
  button__slim: props.slim,
  [`button__${props.version || 'primary'}`]: true,
}));
</script>

<style scoped lang="scss">
//

.button {
  min-width: 280px;
  height: 50px !important;
  color: rgb(var(--v-theme-white));
  background-color: rgb(var(--v-theme-primary));
  font-family: 'brandon-grotesque', sans-serif;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 2px;
  text-align: center;
  border-radius: 4px;
  margin: 0;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background-color: rgb(var(--v-theme-primary-hover));
  }

  &:focus {
    border: 2px solid rgb(var(--v-theme-primary-focus));
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &__slim {
    font-size: 12px;
    height: 40px !important;
    min-width: 220px;
  }

  &__secondary {
    color: rgb(var(--v-theme-secondary-text));
    background-color: rgb(var(--v-theme-secondary));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-secondary-hover));
    }
    &:focus {
      border-color: rgb(var(--v-theme-secondary-focus));
    }
  }
  &__highlight {
    color: rgb(var(--v-theme-highlight-text));
    background-color: rgb(var(--v-theme-highlight));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-highlight-hover));
    }
  }
  &__action {
    color: rgb(var(--v-theme-action-text));
    background-color: rgb(var(--v-theme-action));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-action-hover));
    }
  }
  &__grey {
    color: rgb(var(--v-theme-grey-text));
    background-color: rgb(var(--v-theme-grey));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__white {
    color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-white));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__error {
    color: rgb(var(--v-theme-error-text));
    background-color: rgb(var(--v-theme-error));
    &:hover:not(:disabled) {
      background-color: rgba(var(--v-theme-error), 0.8);
    }
  }
  &__primary--800 {
  color: rgb(var(--v-theme-white));
  background-color: rgb(var(--v-theme-primary-800));
  &:hover:not(:disabled) {
    background-color: rgb(var(--v-theme-primary-800), 0.8);
  }
}

}
</style>
